<template>
  <div>
    <div class="d-flex align-center justify-space-between flex-wrap mb-3">
      <v-tabs v-model="activeTab" show-arrows>
        <v-tab v-for="t in invoiceTabs" :key="t.value">
          {{ t.label }}
          <v-chip x-small class="ml-2" :color="t.badgeColor" :dark="!!t.badgeColor">
            {{ t.badge }}
          </v-chip>
        </v-tab>
      </v-tabs>
      <primary-button v-if="isAdmin" @click="dialogOpen = true">
        <v-icon left small>mdi-plus</v-icon>
        {{ $t("finance_new_invoice") }}
      </primary-button>
    </div>

    <page-toolbar class="mb-3" :searchable="false">
      <template #filters>
        <v-col cols="6" sm="3">
          <dropdown
            v-model="filterPeriod"
            :items="periodOptions"
            item-text="label"
            item-value="value"
            :label="$t('finance_period_label')"
            color="white"
            hide-details
          />
        </v-col>
        <v-col cols="6" sm="3">
          <dropdown
            v-model="filterType"
            :items="typeOptions"
            item-text="label"
            item-value="value"
            :label="$t('finance_type_label')"
            color="white"
            clearable
            hide-details
          />
        </v-col>
        <v-col cols="auto" class="d-flex align-center">
          <v-btn
            v-if="filterPeriod !== 'all' || filterType"
            text
            small
            color="primary"
            @click="resetFilters"
          >
            {{ $t("finance_reset_filters") }}
          </v-btn>
        </v-col>
      </template>
    </page-toolbar>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="filteredInvoices"
        :loading="loading"
        :loading-text="$t('finance_loading_invoices')"
        mobile-breakpoint="0"
        :no-data-text="$t('finance_no_invoices_filtered')"
        @click:row="openInvoice"
      >
        <template #item.number="{ item }">
          <span class="mono font-weight-bold">{{ item.number }}</span>
        </template>
        <template #item.date="{ item }">{{ localeDate(item.date) }}</template>
        <template #item.type="{ item }">{{ $t(INVOICE_TYPE_META[item.type]) }}</template>
        <template #item.amount="{ item }">{{ formatCurrency(item.amount) }}</template>
        <template #item.status="{ item }">
          <v-chip small :color="INVOICE_STATUS_META[item.status].color" dark>
            {{ $t(INVOICE_STATUS_META[item.status].labelKey) }}
          </v-chip>
        </template>
      </v-data-table>
      <p v-if="!loading && filteredInvoices.length > 0" class="hint-copy px-3 pb-3 mb-0">
        {{ $t("finance_click_invoice_details_hint") }}
      </p>
    </v-card>

    <invoice-detail-dialog v-model="detailOpen" :invoice="selectedInvoice" />

    <invoice-form-dialog
      v-if="isAdmin"
      v-model="dialogOpen"
      :saving="saving"
      @save="onSaveInvoice"
    />
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import hasSnackbar from "@/mixins/hasSnackbar";
import InvoiceDetailDialog from "./dialogs/InvoiceDetailDialog.vue";
import InvoiceFormDialog from "./dialogs/InvoiceFormDialog.vue";

export const INVOICE_TYPE_META = {
  commission: "finance_invoice_type_commission",
  marketing: "finance_invoice_type_marketing",
  subscription: "finance_invoice_type_subscription",
  delivery: "finance_invoice_type_delivery",
  correction: "finance_invoice_type_correction",
};

export const INVOICE_STATUS_META = {
  paid: { labelKey: "finance_status_invoice_paid", color: "success" },
  unpaid: { labelKey: "finance_status_invoice_unpaid", color: "warning" },
  overdue: { labelKey: "finance_status_invoice_overdue", color: "error" },
};

// amount/vat dolaze u parama (1/100 KM) - vidi ticket #223672.
const mapInvoiceDto = (dto) => ({
  id: dto.id,
  number: dto.number,
  date: dto.date,
  type: dto.type,
  amount: (Number(dto.amount) || 0) / 100,
  status: dto.status,
  dueDate: dto.due_date,
  paymentMethod: dto.payment_method,
  vat: (Number(dto.vat) || 0) / 100,
  iban: dto.iban,
});

export default {
  name: "FinanceTabInvoices",

  components: { InvoiceDetailDialog, InvoiceFormDialog },

  mixins: [hasSnackbar],

  data: () => ({
    INVOICE_TYPE_META,
    INVOICE_STATUS_META,
    invoices: [],
    loading: true,
    saving: false,
    activeTab: 0,
    filterPeriod: "all",
    filterType: null,
    detailOpen: false,
    selectedInvoice: null,
    dialogOpen: false,
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },
    isAdmin() {
      return this.$store.state.user?.user?.type === "admin";
    },

    headers() {
      return [
        { text: this.$t("finance_number"), value: "number" },
        { text: this.$t("finance_date"), value: "date" },
        { text: this.$t("finance_type_label"), value: "type" },
        { text: this.$t("finance_amount"), value: "amount" },
        { text: this.$t("finance_status_label"), value: "status" },
      ];
    },

    periodOptions() {
      return [
        { label: this.$t("finance_all"), value: "all" },
        { label: this.$t("finance_last_30_days"), value: "30d" },
        { label: this.$t("finance_last_90_days"), value: "90d" },
        { label: this.$t("finance_this_year"), value: "year" },
      ];
    },

    paidCount() {
      return this.invoices.filter((i) => i.status === "paid").length;
    },
    unpaidCount() {
      return this.invoices.filter((i) => i.status === "unpaid" || i.status === "overdue")
        .length;
    },
    overdueCount() {
      return this.invoices.filter((i) => i.status === "overdue").length;
    },

    invoiceTabs() {
      return [
        { value: "all", label: this.$t("finance_all"), badge: this.invoices.length },
        { value: "paid", label: this.$t("finance_tab_paid"), badge: this.paidCount, badgeColor: "success" },
        { value: "unpaid", label: this.$t("finance_tab_unpaid"), badge: this.unpaidCount, badgeColor: "warning" },
        { value: "overdue", label: this.$t("finance_tab_overdue"), badge: this.overdueCount, badgeColor: "error" },
      ];
    },

    typeOptions() {
      return Object.keys(INVOICE_TYPE_META).map((value) => ({
        value,
        label: this.$t(INVOICE_TYPE_META[value]),
      }));
    },

    filteredInvoices() {
      const tabKey = this.invoiceTabs[this.activeTab]?.value || "all";
      const byTab = (invoice) => {
        if (tabKey === "all") return true;
        if (tabKey === "paid") return invoice.status === "paid";
        if (tabKey === "unpaid")
          return invoice.status === "unpaid" || invoice.status === "overdue";
        return invoice.status === "overdue";
      };

      return this.invoices.filter(
        (invoice) =>
          byTab(invoice) &&
          this.isWithinPeriod(invoice.date, this.filterPeriod) &&
          (!this.filterType || invoice.type === this.filterType)
      );
    },
  },

  created() {
    this.loadInvoices();
  },

  methods: {
    async loadInvoices() {
      if (!this.restaurantId) return;

      this.loading = true;
      try {
        const { data } = await restaurantFinanceService.getInvoices(this.restaurantId);
        this.invoices = (data?.data || []).map(mapInvoiceDto);
      } catch (error) {
        this.showErrorSnackbar(
          error?.response?.data?.message || this.$t("finance_invoices_load_error")
        );
      } finally {
        this.loading = false;
      }
    },

    isWithinPeriod(date, value) {
      if (value === "all") return true;

      const target = new Date(date);
      const now = new Date();

      if (value === "year") return target.getFullYear() === now.getFullYear();

      const days = value === "30d" ? 30 : 90;
      return now.getTime() - target.getTime() <= days * 24 * 60 * 60 * 1000;
    },

    resetFilters() {
      this.filterPeriod = "all";
      this.filterType = null;
    },

    openInvoice(invoice) {
      this.selectedInvoice = invoice;
      this.detailOpen = true;
    },

    async onSaveInvoice(payload) {
      this.saving = true;
      try {
        const { data } = await restaurantFinanceService.createInvoice(
          this.restaurantId,
          payload
        );
        this.invoices = [mapInvoiceDto(data.data || data), ...this.invoices];
        this.showSuccessSnackbar(this.$t("finance_invoice_created"));
        this.dialogOpen = false;
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_invoice_create_error"));
      } finally {
        this.saving = false;
      }
    },

    formatCurrency(amount) {
      const value = Number(amount) || 0;
      return `${value.toFixed(2)} ${this.restaurantCurrency}`;
    },
  },
};
</script>

<style scoped>
.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.hint-copy {
  font-size: 0.78rem;
  color: #9aa4b2;
}

::v-deep tbody tr {
  cursor: pointer;
}
</style>
